import { useState, useMemo } from 'react';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import {
  useFetchETFHoldings,
  useCreateETFHolding,
  useUpdateETFHolding,
  useDeleteETFHolding,
} from '@etfSecurityHooks';
import {
  useFetchETFDividends,
  useCreateETFDividend,
} from '@etfSecurityHooks';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Plus, DollarSign, BarChart3, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { ETFForm } from '@/components/etf-form/ETFForm';
import { ETFDividendForm } from '@/components/etf-dividend-form/ETFDividendForm';
import { ETFMetricsCards } from '@/components/etf-metrics-cards/ETFMetricsCards';
import { ETFHoldingsTable } from '@/components/etf-holdings-table/ETFHoldingsTable';
import { ETFPortfolioTrendChart } from '@/components/etf-portfolio-trend-chart/ETFPortfolioTrendChart';
import { ETFDividendTimeline, enrichDividendsWithTicker } from '@/components/etf-dividend-timeline/ETFDividendTimeline';
import { PageHeaderSkeleton, AccountCardSkeleton, ChartSkeleton } from '@/components/LoadingSkeletons';
import { useNavigate } from 'react-router-dom';
import {ETFHolding} from "@/integrations/supabase/etf.models.ts";

/**
 * ETFsPage displays and manages ETF holdings, metrics, trend chart, and dividend timeline.
 * Accessible at /investments/securities/etfs.
 */
export default function ETFsPage() {
  const [formOpen, setFormOpen] = useState(false);
  const [dividendFormOpen, setDividendFormOpen] = useState(false);
  const [editingHolding, setEditingHolding] = useState<ETFHolding | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [preselectedHoldingId, setPreselectedHoldingId] = useState<string | undefined>();
  const [statusMessage, setStatusMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const navigate = useNavigate();

  const { data: holdings = [], isLoading, error, refetch } = useFetchETFHoldings();
  const { data: allDividends = [] } = useFetchETFDividends();

  const createHolding = useCreateETFHolding();
  const updateHolding = useUpdateETFHolding();
  const deleteHolding = useDeleteETFHolding();
  const createDividend = useCreateETFDividend();

  // Build prices map (empty — ETFMetricsCards and ETFHoldingsTable fetch prices internally via ETFPriceDisplay)
  const prices = useMemo(() => ({}), []);

  // Enrich dividends with ticker symbols for the timeline
  const enrichedDividends = useMemo(
    () => enrichDividendsWithTicker(allDividends, holdings),
    [allDividends, holdings]
  );

  const filteredHoldings = useMemo(() => {
    if (!searchQuery) return holdings;
    const lowerQuery = searchQuery.toLowerCase();
    return holdings.filter(h => 
      h.ticker_symbol?.toLowerCase().includes(lowerQuery) ||
      h.etf_name?.toLowerCase().includes(lowerQuery) ||
      h.category?.toLowerCase().includes(lowerQuery) ||
      h.notes?.toLowerCase().includes(lowerQuery) ||
      h.id?.toLowerCase().includes(lowerQuery)
    );
  }, [holdings, searchQuery]);

  const handleSaveHolding = async (data: ETFHoldingCreateRequest) => {
    try {
      if (editingHolding) {
        await updateHolding.mutateAsync({ id: editingHolding.id, ...data });
        toast.success('ETF holding updated');
        setStatusMessage('ETF holding updated');
      } else {
        await createHolding.mutateAsync(data);
        toast.success('ETF holding added');
        setStatusMessage('ETF holding added');
      }
      setFormOpen(false);
      setEditingHolding(null);
    } catch {
      const msg = editingHolding ? 'Failed to update ETF holding' : 'Failed to add ETF holding';
      toast.error(msg);
      setStatusMessage(msg);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deletingId) return;
    try {
      await deleteHolding.mutateAsync(deletingId);
      toast.success('ETF holding deleted');
      setStatusMessage('ETF holding deleted');
      setDeletingId(null);
    } catch {
      toast.error('Failed to delete ETF holding');
    }
  };

  const handleSaveDividend = async (data: ETFDividendCreateRequest) => {
    try {
      await createDividend.mutateAsync(data);
      toast.success('Dividend recorded');
      setStatusMessage('Dividend recorded');
      setDividendFormOpen(false);
      setPreselectedHoldingId(undefined);
    } catch {
      toast.error('Failed to record dividend');
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6" aria-busy="true" aria-live="polite" data-testid="loading-skeleton">
        <PageHeaderSkeleton />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => <AccountCardSkeleton key={i} />)}
        </div>
        <AccountCardSkeleton />
        <ChartSkeleton />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <p className="text-destructive">Failed to load ETF holdings.</p>
        <Button variant="outline" onClick={() => refetch()}>Retry</Button>
      </div>
    );
  }

  const hasHoldings = holdings.length > 0;

  return (
    <ErrorBoundary onReset={() => window.location.reload()}>
      <div className="space-y-6">
        {/* SR live region */}
        <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
          {statusMessage}
        </div>

        {/* Header */}
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">ETFs</h1>
            <p className="text-sm text-muted-foreground sm:text-base">
              Track your Exchange-Traded Fund holdings and dividend income
            </p>
          </div>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:flex-wrap w-full lg:w-auto">
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search ETFs..."
                className="pl-8"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-2 sm:flex-row w-full sm:w-auto" role="group" aria-label="ETF actions">
              <Button
                variant="outline"
                onClick={() => { setPreselectedHoldingId(undefined); setDividendFormOpen(true); }}
                disabled={!hasHoldings}
                aria-disabled={!hasHoldings}
                className="min-h-[44px] w-full sm:w-auto"
              >
                <DollarSign className="mr-2 h-4 w-4" aria-hidden="true" />
                Record Dividend
              </Button>
              <Button
                onClick={() => { setEditingHolding(null); setFormOpen(true); }}
                className="min-h-[44px] w-full sm:w-auto"
                aria-label="Add new ETF holding"
              >
                <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
                Add ETF
              </Button>
            </div>
          </div>
        </div>

        {/* Metrics Cards */}
        {hasHoldings && (
          <ETFMetricsCards holdings={filteredHoldings} prices={prices} isLoading={false} />
        )}

        {/* Holdings Table or Empty State */}
        {hasHoldings ? (
          <>
            <ETFHoldingsTable
              holdings={filteredHoldings}
              prices={prices}
              onEdit={(holding) => { setEditingHolding(holding); setFormOpen(true); }}
              onDelete={(holding) => setDeletingId(holding.id)}
              onRowClick={(holding) => navigate(`/investments/securities/etfs/${holding.id}`)}
            />

            {/* Portfolio Trend Chart */}
            <ETFPortfolioTrendChart />

            {/* Dividend Timeline */}
            <ETFDividendTimeline dividends={enrichedDividends} />
          </>
        ) : (
          <Card role="status" aria-label="Empty state">
            <CardContent className="flex flex-col items-center justify-center py-12 px-4 text-center">
              <BarChart3 className="h-12 w-12 text-muted-foreground mb-4" aria-hidden="true" />
              <p className="text-base sm:text-lg text-muted-foreground mb-2">No ETF holdings yet</p>
              <p className="text-xs sm:text-sm text-muted-foreground mb-6">
                Add your first ETF to start tracking performance, yield, and dividend income
              </p>
              <Button
                onClick={() => { setEditingHolding(null); setFormOpen(true); }}
                className="min-h-[44px] w-full sm:w-auto"
              >
                <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
                Add ETF
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Add/Edit Dialog */}
        <Dialog open={formOpen} onOpenChange={open => { setFormOpen(open); if (!open) setEditingHolding(null); }}>
          <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[600px] w-[95vw] sm:w-full">
            <DialogHeader>
              <DialogTitle>{editingHolding ? 'Edit ETF Holding' : 'Add ETF Holding'}</DialogTitle>
              <DialogDescription>
                {editingHolding ? 'Update the details of your ETF holding.' : 'Add a new ETF to your portfolio.'}
              </DialogDescription>
            </DialogHeader>
            <ETFForm
              holding={editingHolding ?? undefined}
              onSave={handleSaveHolding}
              onCancel={() => { setFormOpen(false); setEditingHolding(null); }}
            />
          </DialogContent>
        </Dialog>

        {/* Dividend Dialog */}
        <Dialog open={dividendFormOpen} onOpenChange={open => { setDividendFormOpen(open); if (!open) setPreselectedHoldingId(undefined); }}>
          <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[500px] w-[95vw] sm:w-full">
            <DialogHeader>
              <DialogTitle>Record Dividend</DialogTitle>
              <DialogDescription>Log a dividend payment received from an ETF holding.</DialogDescription>
            </DialogHeader>
            <ETFDividendForm
              holdings={holdings}
              preselectedHoldingId={preselectedHoldingId}
              onSave={handleSaveDividend}
              onCancel={() => { setDividendFormOpen(false); setPreselectedHoldingId(undefined); }}
            />
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={!!deletingId} onOpenChange={open => !open && setDeletingId(null)}>
          <AlertDialogContent className="w-[95vw] sm:w-full sm:max-w-[500px]">
            <AlertDialogHeader>
              <AlertDialogTitle>Delete ETF holding?</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently delete the holding and all associated dividend records. This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="flex-col gap-2 sm:flex-row">
              <AlertDialogCancel className="min-h-[44px] w-full sm:w-auto">Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleConfirmDelete}
                className="min-h-[44px] w-full sm:w-auto bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </ErrorBoundary>
  );
}
